import React, {useMemo, useState} from 'react';
import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {TabPanel, TabView} from 'primereact/tabview';
import RegularAccordion from '../Components/RegularAccordion';
import FavoriteAccordion from '../Components/FavoriteAccordion';
import PageHeading from "../../../../Components/PageHeading";
import {reportsData} from "../../Data/reportData";
import {ReportItemData} from "../../../../types/demo";

const Report = () => {
    const [favorites, setFavorites] = useState<ReportItemData[]>([]);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});

    const moduleTabs = useMemo(() => {
        const moduleMap = reportsData.reduce((acc, item) => {
            const modName = item.module || "General";
            if (!acc[modName]) acc[modName] = [];
            acc[modName].push(item);
            return acc;
        }, {} as Record<string, ReportItemData[]>);

        const individualModules = Object.entries(moduleMap).map(([moduleName, items]) => ({
            moduleName,
            groups: items.reduce((acc, item) => {
                if (!acc[item.type]) acc[item.type] = [];
                acc[item.type].push(item);
                return acc;
            }, {} as Record<string, ReportItemData[]>)
        }));

        const allGroups = reportsData.reduce((acc, item) => {
            if (!acc[item.type]) acc[item.type] = [];
            acc[item.type].push(item);
            return acc;
        }, {} as Record<string, ReportItemData[]>);

        return [{moduleName: "All", groups: allGroups}, ...individualModules];
    }, []);


    const isMajorityExpanded = useMemo(() => {
        const currentTabGroups = Object.keys(moduleTabs[activeTab].groups);
        const totalGroups = currentTabGroups.length;

        const openCount = currentTabGroups.filter(title =>
            expandedMap[`${moduleTabs[activeTab].moduleName}-${title}`]
        ).length;

        if (totalGroups === 0) return true;
        return (openCount / totalGroups) >= 0.6;
    }, [expandedMap, activeTab, moduleTabs]);

    const toggleAll = () => {
        const targetState = !isMajorityExpanded;
        const currentTabName = moduleTabs[activeTab].moduleName;
        const currentTabGroups = Object.keys(moduleTabs[activeTab].groups);

        const newMap = {...expandedMap};
        currentTabGroups.forEach(title => {
            newMap[`${currentTabName}-${title}`] = targetState;
        });

        // Also sync favorites
        newMap['favorites-section'] = targetState;

        setExpandedMap(newMap);
    };

    const handleAccordionToggle = (key: string, isOpen: boolean) => {
        setExpandedMap(prev => ({...prev, [key]: isOpen}));
    };

    const renderTabContent = (moduleGroups: Record<string, ReportItemData[]>, moduleKey: string) => {
        const q = search.toLowerCase().trim();
        const filtered = Object.entries(moduleGroups)
            .map(([title, items]) => {
                const filteredItems = items.filter(item =>
                    item.label.toLowerCase().includes(q) || item.type.toLowerCase().includes(q)
                );
                if (title.toLowerCase().includes(q) || filteredItems.length > 0) {
                    return {title, items: filteredItems.length > 0 ? filteredItems : items};
                }
                return null;
            }).filter(Boolean) as { title: string, items: ReportItemData[] }[];

        const mid = Math.ceil(filtered.length / 2);
        return (
            <div className="grid pt-3">
                {[filtered.slice(0, mid), filtered.slice(mid)].map((column, colIdx) => (
                    <div key={colIdx} className="col-12 md:col-6 flex flex-column gap-3">
                        {column.map((group) => {
                            const uniqueKey = `${moduleKey}-${group.title}`;
                            return (
                                <RegularAccordion
                                    key={uniqueKey}
                                    header={group.title}
                                    items={group.items}
                                    favorites={favorites}
                                    onToggleFavorite={item => {
                                        setFavorites(prev => {
                                            const isFav = prev.some(f => f.label === item.label);
                                            return isFav ? prev.filter(f => f.label !== item.label) : [...prev, item];
                                        });
                                    }}
                                    isOpen={search ? true : expandedMap[uniqueKey]}
                                    onToggle={(isOpen: boolean) => handleAccordionToggle(uniqueKey, isOpen)}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="">
            <div className="flex flex-column md:flex-row md:justify-content-between md:items-center mb-3 gap-3">
                <PageHeading title="Fleet Reports"/>
                <div className="flex flex-column md:flex-row md:align-items-center gap-2 w-full md:w-auto">
                    <span className="p-input-icon-left w-full md:w-auto">
                        <i className="pi pi-search"/>
                        <InputText
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search for reports"
                            className="w-full sm:w-16rem h-3rem"
                            type='search'
                        />
                    </span>
                    <Button
                        label={isMajorityExpanded ? "Collapse All" : "Expand All"}
                        icon={isMajorityExpanded ? "pi pi-minus" : "pi pi-plus"}
                        size="small"
                        outlined
                        className={`h-3rem ${isMajorityExpanded ? "p-button-secondary" : "p-button-primary"}`}
                        onClick={toggleAll}
                    />
                </div>
            </div>


            {favorites.length > 0 && (
                <div className="mb-4">
                    <FavoriteAccordion
                        items={favorites}
                        onToggleFavorite={(item: any) => {
                            setFavorites(prev => {
                                const isFav = prev.some(f => f.label === item.label);
                                return isFav ? prev.filter(f => f.label !== item.label) : [...prev, item];
                            });
                        }}
                        isOpen={expandedMap['favorites-section']}
                        onToggle={(isOpen: boolean) => handleAccordionToggle('favorites-section', isOpen)}
                    />
                </div>
            )}


                <TabView activeIndex={activeTab}  onTabChange={(e) => setActiveTab(e.index)}>
                    {moduleTabs.map((tab) => (
                        <TabPanel key={tab.moduleName} header={tab.moduleName}>
                            {renderTabContent(tab.groups, tab.moduleName)}
                        </TabPanel>
                    ))}
                </TabView>

        </div>
    );
};

export default Report;